import React, { useEffect, useState } from 'react'
import { useEffectOnce } from 'react-use'
import PropTypes from 'prop-types'
import { connect as reduxConnect, useDispatch, useSelector } from 'react-redux'
import classnames from 'classnames'
import { ErrorBoundary } from '@sentry/react'

import {
  LayoutComponent,
  WindowTitleComponent,
} from '../../../../sharedLegacy/components'
import { useSpeakersQuery } from '../../../../sharedLegacy/hooks/useSpeakersQuery'
import useFeatures from '../../../../sharedLegacy/hooks/useFeatures'
import { useIsJoiningToSpeak } from '../../../../sharedLegacy/join-to-speak'
import WebinarConnectingComponent from '../../WebinarConnectingComponent/WebinarConnectingComponent'
import { getWebinarTitle } from '../../../selectors'
import {
  initWebinar as initWebinarAction,
  setParticipantIds as setParticipantIdsAction,
  fetchScheduleSuccess,
  actionNames,
} from '../../../actions'
import useWebinarAppState from '../../../providers/WebinarAppStateProvider'
import WebinarRoomMonitoring from '../../WebinarRoomMonitoring/WebinarRoomMonitoring'
import ProducerPlaybackControls from '../../ProducerPlaybackControls/ProducerPlaybackControls'
import ProducerOverlayControls from '../../ProducerOverlayControls/ProducerOverlayControls'
import usePlaybackStagedVideos from '../../../hooks/usePlaybackStagedVideos'
import WebinarLiveSync from '../../WebinarLiveSync'
import { Tray } from '../../Tray/Tray'
import { useIsInitializedState } from '../../../../firebase/hooks/useWebinarGlobalState'
import { useConnectInfoQuery } from '../../../../global/blocs/useConnectInfo'
import { useRoomGlobalInfo } from '../../../../global/blocs/roomInfo'
import {
  useParticipants,
  useLocalParticipant,
  connect,
} from '../../../../webrtc/webRTCRoom'
import { useSchedule } from '../../../store/schedule/useSchedule'
import { NoticesBanner } from '../../../../Layout/Notices/NoticesBanner'
import { Forewarn } from '../../../../Forewarn/Forewarn'
import { useWebinarColors } from '../../../../Designs/blocs/useWebinarColors'

import Sidebar from './Sidebar'
import WebinarRoomContent from './WebinarRoomContent'
import Connecting from './Connecting'

import './WebinarRoom.scss'

function getConnectingModal(isJoiningToSpeak) {
  return isJoiningToSpeak ? (
    <WebinarConnectingComponent
      isModalOpen
      loadingText="Getting you up to speed with the other Speakers!"
    />
  ) : (
    <Connecting />
  )
}

const { Content, Sider } = LayoutComponent
const getLiveDataState = (state) => state.webinar.liveData

const WebinarRoom = ({
  /** Connected properties */
  isInGearMode,
  webinarTitle,
  /** Connected functions */
  initWebinar,
}) => {
  const { liveId, presenterToken } = useRoomGlobalInfo()
  const dispatch = useDispatch()
  const { scope: role } = useWebinarAppState()
  const isSignalingInitialized = useIsInitializedState()

  const { data: webinarInfo } = useConnectInfoQuery()

  const localParticipant = useLocalParticipant()
  const participants = useParticipants((p) => !p.isLocal)
  const liveData = useSelector(getLiveDataState)
  const isConnectingModalVisible = useSelector(
    (state) => state.webinar.isConnectingModalVisible
  )
  const { data: schedule } = useSchedule(liveId, webinarInfo.token)
  const [isSyncReady, setIsSyncReady] = useState(false)
  const { HasWebinarTrayEnabled } = useFeatures()
  const [isJoiningToSpeak] = useIsJoiningToSpeak(`${liveId}`)

  const { data: speakers, refetch: refetchSpeakers } = useSpeakersQuery()

  const isProducer = role === 'producer'
  const isPresenter = role === 'presenter'
  const isGuest =
    isPresenter &&
    speakers?.find((speaker) => presenterToken.endsWith(speaker.id))
      ?.creationSource === 'guest'

  const { loadPlaybackVideos } = usePlaybackStagedVideos()
  const { background } = useWebinarColors()

  useEffect(() => {
    initWebinar({ liveId, role, debug: false, presenterToken })
  }, [liveId, role, initWebinar, presenterToken])

  useEffectOnce(() => {
    connect().then(() => {
      dispatch({ type: actionNames.TWILIO_CONNECTED })
      dispatch({ type: actionNames.CONNECT_TO_WEBINAR })
    })
  })

  /*
   * Legacy: update Redux store with connect info
   * TODO: delete when Redux dependency is removed
   */
  useEffect(() => {
    if (!webinarInfo) {
      return
    }

    dispatch({
      type: actionNames.FETCH_CONNECTION_INFO_SUCCESS,
      authInfo: {
        webrtc_room: webinarInfo.webrtc_room,
        webrtc_authenticator: webinarInfo.webrtc_authenticator,
        webrtc_data_authenticator: webinarInfo.webrtc_data_authenticator,
      },
      presenterToken: webinarInfo.presenter_token,
      liveAuthToken: webinarInfo.token,
      firebaseToken: webinarInfo.webrtc_data_authenticator,
      siteLogo: webinarInfo.custom_logo_url,
      streaming_method: webinarInfo.streaming_method,
      liveSpeakerId: webinarInfo.live_speaker_id,
      isGearMode: webinarInfo.streaming_method === 'hardware',
    })
  }, [dispatch, webinarInfo])

  useEffect(() => {
    const participantIds = participants.map((participant) => participant.id)
    dispatch(setParticipantIdsAction(participantIds))
  }, [participants, dispatch])

  const liveDataToken = liveData?.token

  // TODO: include remote participants as dependency
  useEffect(() => {
    if (liveId && liveDataToken) {
      refetchSpeakers()
    }
  }, [liveDataToken, liveId, refetchSpeakers])

  useEffect(() => {
    if (schedule) {
      dispatch(fetchScheduleSuccess(schedule))
    }
  }, [dispatch, schedule])

  // NOTE (kronborg): Pre-fetch staged videos for video playback. This would
  // otherwise only happen when the playback drawer is opened, because that is
  // when the feature loads for the first time. But coming from a refresh, we
  // can not assume that the producer opens the playback drawer before starting
  // the playback.
  useEffect(() => {
    if (role === 'producer') {
      loadPlaybackVideos()
    }
  }, [role, loadPlaybackVideos])

  useEffect(() => {
    if (role === 'producer' && isSignalingInitialized && localParticipant) {
      setIsSyncReady(true)
    }
  }, [role, isSignalingInitialized, localParticipant])

  const className = classnames(`webinar-container-component role-${role}`, {
    'is-gear-mode': isInGearMode,
  })

  return (
    <>
      <WebinarRoomMonitoring />
      {localParticipant && isProducer && isSyncReady && <WebinarLiveSync />}
      <div className={className} style={{ backgroundColor: background.color }}>
        <WindowTitleComponent title={webinarTitle} />
        <LayoutComponent>
          {HasWebinarTrayEnabled && isProducer && (
            <ErrorBoundary>
              <Tray speakers={speakers} />
            </ErrorBoundary>
          )}
          <Content>
            {isConnectingModalVisible &&
              role !== 'producer' &&
              getConnectingModal(isJoiningToSpeak)}

            <NoticesBanner />

            <WebinarRoomContent
              isProducer={isProducer}
              isPresenter={isPresenter}
              isGuest={isGuest}
            />
          </Content>
          <Sider width={400} className="webinar-sider">
            <Sidebar />
          </Sider>
        </LayoutComponent>
      </div>
      {/* TODO: remove this — This is all business logic running as a react component; it should live outside of react entirely */}
      {isProducer && liveId && <ProducerPlaybackControls liveId={liveId} />}

      {isProducer && <ProducerOverlayControls />}
      <Forewarn />
    </>
  )
}

WebinarRoom.propTypes = {
  /**
   * Connected properties
   */
  isInGearMode: PropTypes.bool,
  webinarTitle: PropTypes.string,

  /**
   * Connected functions
   */
  initWebinar: PropTypes.func,
}

const mapStateToProps = (state) => ({
  /**
   * Connected properties
   */
  role: state.webinar.role,
  isInGearMode: state.webinar.isGearMode,
  webinarTitle: getWebinarTitle(state),
})

const mapDispatchToProps = {
  /**
   * Connected functions
   */
  initWebinar: initWebinarAction,
}

export default reduxConnect(mapStateToProps, mapDispatchToProps)(WebinarRoom)
